
import React, { useEffect, useMemo } from 'react';
import { GenericScheduleProps, Schedule12IPData, Schedule12IPItem, FormErrorsStep } from '../../types';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { safeParseFloat } from '../../utils/calculations';

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4 className="text-md font-semibold text-sky-700 mt-4 mb-2 border-b pb-1">{title}</h4>
);

export const Schedule12_2_FreeZoneIP: React.FC<GenericScheduleProps<Schedule12IPData>> = ({ scheduleData, onChange, errors, t, fullFormData }) => {
  
  const handleItemChange = (index: number, field: keyof Schedule12IPItem, value: string) => {
    const currentIpItems = scheduleData.ipItems || [];
    const updatedItems = currentIpItems.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    onChange('ipItems', updatedItems);
  };

  const addItem = () => {
    const currentIpItems = scheduleData.ipItems || [];
    onChange('ipItems', [
      ...currentIpItems,
      { 
        id: Date.now().toString(), 
        description: '', 
        overallIncome: '', 
        overallExpenditure: '', 
        qualifyingExpenditure: '' 
      }
    ]);
  };

  const removeItem = (id: string) => {
    const currentIpItems = scheduleData.ipItems || [];
    onChange('ipItems', currentIpItems.filter(item => item.id !== id));
  };

  useEffect(() => {
    let totalIncomeInExcess = 0;
    let totalRevenueQualifyingIP = 0;
    const currentIpItems = scheduleData.ipItems || [];

    const updatedIpItems = currentIpItems.map(item => {
      const overallIncome = safeParseFloat(item.overallIncome);
      const overallExpenditure = safeParseFloat(item.overallExpenditure);
      const qualifyingExpenditure = safeParseFloat(item.qualifyingExpenditure);

      const oeMinusQe = overallExpenditure - qualifyingExpenditure;
      const calculatedUpliftExpenditure = Math.min(qualifyingExpenditure * 0.3, Math.max(0, oeMinusQe));

      let revenueAttrToQI = 0;
      if (overallExpenditure > 0) {
        revenueAttrToQI = overallIncome * ((qualifyingExpenditure + calculatedUpliftExpenditure) / overallExpenditure);
      } else if (overallIncome > 0 && qualifyingExpenditure > 0) { // If no expenditure, but income and QE, then all income from that QE is qualifying
        revenueAttrToQI = overallIncome;
      }
      
      const incomeInExcessOfQI = overallIncome - revenueAttrToQI;

      totalIncomeInExcess += incomeInExcessOfQI;
      totalRevenueQualifyingIP += revenueAttrToQI;
      
      return {
        ...item,
        upliftExpenditure: calculatedUpliftExpenditure.toFixed(2),
        revenueAttrToQI: revenueAttrToQI.toFixed(2),
        incomeInExcessOfQI: incomeInExcessOfQI.toFixed(2),
      };
    });

    const totalRevenueFromIPNotQualifying = totalIncomeInExcess + safeParseFloat(scheduleData.totalIncomeFromOtherIPNonQualifying);

    let changed = false;
    if (JSON.stringify(updatedIpItems) !== JSON.stringify(currentIpItems)) {
        onChange('ipItems', updatedIpItems);
        changed = true;
    }
    if (scheduleData.totalIncomeInExcessOfQI !== totalIncomeInExcess.toFixed(2)) {
        onChange('totalIncomeInExcessOfQI', totalIncomeInExcess.toFixed(2));
        changed = true;
    }
    if (scheduleData.totalRevenueFromIPNotQualifyingIncome !== totalRevenueFromIPNotQualifying.toFixed(2)) {
        onChange('totalRevenueFromIPNotQualifyingIncome', totalRevenueFromIPNotQualifying.toFixed(2));
        changed = true;
    }
    if (scheduleData.totalRevenueFromQualifyingIP !== totalRevenueQualifyingIP.toFixed(2)) {
        onChange('totalRevenueFromQualifyingIP', totalRevenueQualifyingIP.toFixed(2));
        changed = true;
    }

  }, [scheduleData.ipItems, scheduleData.totalIncomeFromOtherIPNonQualifying, onChange, scheduleData.totalIncomeInExcessOfQI, scheduleData.totalRevenueFromIPNotQualifyingIncome, scheduleData.totalRevenueFromQualifyingIP]);


  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-sky-600">{t('sch12_2_title')}</h3>
      
      {(scheduleData.ipItems || []).map((item, index) => (
        <div key={item.id} className="p-3 border rounded-md space-y-3 bg-slate-50 relative">
            <p className="text-sm font-semibold text-gray-600">{t('sch12_2_ip_item_heading', {index: index + 1})}</p>
            <Input label={t('sch12_2_ip_item_description')} type="text" id={`ipDesc-${item.id}`} value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} error={(errors?.ipItems as FormErrorsStep[])?.[index]?.description as string}/>
            <Input label={t('sch12_2_overall_income_from_qip')} type="number" id={`ipOverallIncome-${item.id}`} value={item.overallIncome} onChange={(e) => handleItemChange(index, 'overallIncome', e.target.value)} error={(errors?.ipItems as FormErrorsStep[])?.[index]?.overallIncome as string}/>
            <Input label={t('sch12_2_overall_expenditure_qip')} type="number" id={`ipOverallExp-${item.id}`} value={item.overallExpenditure} onChange={(e) => handleItemChange(index, 'overallExpenditure', e.target.value)} error={(errors?.ipItems as FormErrorsStep[])?.[index]?.overallExpenditure as string}/>
            <Input label={t('sch12_2_qualifying_expenditure_qip')} type="number" id={`ipQualExp-${item.id}`} value={item.qualifyingExpenditure} onChange={(e) => handleItemChange(index, 'qualifyingExpenditure', e.target.value)} error={(errors?.ipItems as FormErrorsStep[])?.[index]?.qualifyingExpenditure as string}/>
            <Input label={t('sch12_2_uplift_expenditure_qip')} type="text" id={`ipUpliftExp-${item.id}`} value={item.upliftExpenditure || '0.00'} readOnly className="bg-gray-100"/>
            <Input label={t('sch12_2_revenue_attr_qi')} type="text" id={`ipRevAttrQI-${item.id}`} value={item.revenueAttrToQI || '0.00'} readOnly className="bg-gray-100"/>
            <Input label={t('sch12_2_income_excess_qi')} type="text" id={`ipIncomeExcessQI-${item.id}`} value={item.incomeInExcessOfQI || '0.00'} readOnly className="bg-gray-100"/>
            <Button onClick={() => removeItem(item.id)} variant="danger_outline" size="sm" className="absolute top-2 right-2">{t('remove_item_button')}</Button>
        </div>
      ))}
      <Button onClick={addItem} variant="secondary" size="sm">{t('sch12_2_add_ip_item_button')}</Button>

      <Input label={t('sch12_2_total_income_excess_qi')} type="text" id="s12TotalIncomeExcessQI" value={scheduleData.totalIncomeInExcessOfQI || '0.00'} readOnly className="bg-gray-100 font-semibold" />
      <Input label={t('sch12_2_total_income_other_ip_non_qual')} type="number" id="s12TotalIncomeOtherIPNonQual" value={scheduleData.totalIncomeFromOtherIPNonQualifying} onChange={(e) => onChange('totalIncomeFromOtherIPNonQualifying', e.target.value)} error={errors?.totalIncomeFromOtherIPNonQualifying as string} />
      <Input label={t('sch12_2_total_revenue_ip_not_qi')} type="text" id="s12TotalRevenueIPNotQI" value={scheduleData.totalRevenueFromIPNotQualifyingIncome || '0.00'} readOnly className="bg-gray-100 font-semibold" />
      <Input label={t('fz_D4_revenue_ip_qualifying')} type="text" id="s12TotalRevenueQualifyingIP" value={scheduleData.totalRevenueFromQualifyingIP || '0.00'} readOnly className="bg-gray-100 font-semibold" />

    </div>
  );
};
